import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { api } from '@/lib/axios'

type Rechteck = { links: number; oben: number; breite: number; hoehe: number }

/**
 * AP-37 Detailblatt N 9. (N-P1) — **Fundstelle**: die PDF-Seite einer Unterlage mit markiertem
 * Rechteck (Plan) bzw. hervorgehobenem Zitat. Gerendert **im Browser** mit pdf.js (KI-58) —
 * der Container hat keinen Canvas. Koordinaten in PDF-Punkten, Ursprung oben links (3a.3).
 */
export function FundstelleDialog({
  kalkulationId,
  fileId,
  seite,
  rect,
  zitat,
  onClose,
}: {
  kalkulationId: string
  fileId: string
  seite: number
  rect?: readonly number[] | null
  zitat?: string | null
  onClose: () => void
}) {
  const leinwand = useRef<HTMLCanvasElement>(null)
  const [marken, setMarken] = useState<Rechteck[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    let aus = false
    void (async () => {
      try {
        const [pdfjs, worker, daten] = await Promise.all([
          import('pdfjs-dist'),
          import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
          api.get<ArrayBuffer>(`/calculations/${kalkulationId}/files/${fileId}/content`, { responseType: 'arraybuffer' }).then((r) => r.data),
        ])
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default
        const doc = await pdfjs.getDocument({ data: new Uint8Array(daten), isEvalSupported: false }).promise
        const page = await doc.getPage(Math.min(Math.max(1, seite), doc.numPages))
        const basis = page.getViewport({ scale: 1 })
        const scale = Math.min(2, 860 / basis.width)
        const vp = page.getViewport({ scale })
        const c = leinwand.current
        if (!c || aus) return
        c.width = vp.width
        c.height = vp.height
        await page.render({ canvasContext: c.getContext('2d')!, viewport: vp }).promise
        const neu: Rechteck[] = []
        if (rect && rect.length === 4) {
          const [x0, y0, x1, y1] = rect
          neu.push({ links: Math.min(x0, x1) * scale, oben: Math.min(y0, y1) * scale, breite: Math.abs(x1 - x0) * scale, hoehe: Math.abs(y1 - y0) * scale })
        } else if (zitat) {
          // Ohne Rechteck: die Textstücke hervorheben, die das Zitat enthalten.
          const norm = (t: string) => t.toLowerCase().replace(/\s+/g, '')
          const gesucht = norm(zitat)
          const inhalt = await page.getTextContent()
          for (const it of inhalt.items as { str: string; transform: number[]; width: number; height: number }[]) {
            const stueck = norm(it.str ?? '')
            if (!stueck || !gesucht || !(stueck.includes(gesucht) || (gesucht.includes(stueck) && stueck.length > 3))) continue
            const h = it.height || Math.abs(it.transform[3])
            neu.push({ links: it.transform[4] * scale, oben: (basis.height - it.transform[5] - h) * scale, breite: it.width * scale, hoehe: h * scale })
          }
        }
        if (!aus) setMarken(neu)
      } catch (e) {
        if (!aus) setFehler(apiErrorText(e, 'Die Unterlage ließ sich nicht anzeigen.'))
      } finally {
        if (!aus) setLaedt(false)
      }
    })()
    return () => {
      aus = true
    }
  }, [kalkulationId, fileId, seite, rect, zitat])

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={onClose}>
      <div
        className="my-6 space-y-2 rounded-lg border border-border p-3 shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 text-sm">
          <span>
            Fundstelle — Seite {seite}
            {zitat ? ` · „${zitat}"` : ''}
          </span>
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={onClose} aria-label="Schließen">
            <X className="h-4 w-4" />
          </button>
        </div>
        {laedt && <p className="text-sm text-muted-foreground">Lädt …</p>}
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        <div className="max-w-[calc(100vw-4rem)] overflow-auto">
        <div className="relative inline-block">
          <canvas ref={leinwand} className="block" />
          {marken.map((m, i) => (
            <span
              key={i}
              className="pointer-events-none absolute rounded-sm border-2 border-amber-500 bg-amber-400/25"
              style={{ left: m.links - 2, top: m.oben - 2, width: m.breite + 4, height: m.hoehe + 4 }}
            />
          ))}
        </div>
        </div>
        {!laedt && !fehler && (rect || zitat) && !marken.length && <p className="text-xs text-amber-700 dark:text-amber-400">Das Zitat steht so nicht auf dieser Seite.</p>}
      </div>
    </div>
  )
}
